/**
 * @fileOverview The main entry point to your standalone. Start editing here.
 */

import React, { useEffect, useState } from 'react'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/reset.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/site.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/segment.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/container.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/header.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/image.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/list.css'
import '@ta-interaktiv/semantic-ui/semantic/dist/components/label.css'
import './styles.scss'
import { TypescriptComponent } from 'components/typescriptExample'
import { Masthead } from '@ta-interaktiv/react-masthead'
import { Footer } from '@ta-interaktiv/react-footer'
import { ThemeSwitch } from '@ta-interaktiv/react-theme-switch'
import { Link } from '@ta-interaktiv/contentbricks'
import { qanda } from './qanda'
import {
  EstimateQuestion,
  SingleChoice,
  Slider,
  SwissTileMap,
  ColumnChart,
  BarChart,
  Summary,
  SwissTileResolve,
  voteComparison,
  voteQuality,
  convertPercentToWords,
  capitalize
} from 'components/UserEstimates'
import { GlobalColors } from 'components/taCSScolors'

// TODO Set the correct article ID here
const ARTICLE_ID = '615097592989'

const nf = new Intl.NumberFormat('de-CH')

export function AppComponent() {
  const [questionAnswered, setAnswered] = useState<string[]>([])
  const handleAnswer = (questionid: string) => {
    setAnswered(questionAnswered => [...questionAnswered, questionid])
  }

  const valueFormat = (val: any) => {
    return val > 9999 ? nf.format(parseInt(val)) : val
  }

  return (
    <div className='index'>
      <Masthead articleId={ARTICLE_ID} />
      <ThemeSwitch />
      <GlobalColors />

      <header className='ui vertical  very fitted  segment'>
        <div className='ui container'>
          <div className='ui header'>
            <h1>
              Bestechungs&shy;skandal: Schätzen Sie den Wert dieser Luxusuhren
            </h1>
            <h2>
              Im Venezuela-Korruptionsfall flossen 9 Milliarden durch die
              Schweiz, während das Volk hungert. Bestochen wurde unter anderem
              mit Schweizer Luxusuhren. Versuchen Sie mal, deren Wert zu
              schätzen.
            </h2>
          </div>
        </div>
      </header>

      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta metainfo'>
            <div className='ta author'>
              Christian Brönnimann, Sylvain Besson, Patrick Vögeli
            </div>
            <div className='ta updated'>Aktualisiert am 16. Januar 2021</div>
          </div>
          <div className='ta text'>
            <p>
              Der korrupte ehemalige Schatzmeister von Venezuela, Alejandro
              Andrade, häufte einen unvorstellbaren Reichtum an. 2018
              verurteilten ihn die USA zu zehn Jahren Haft. Er unterzeichnete
              ein Schuldeingeständnis, wonach er mit einer Milliarde Dollar
              bestochen wurde, teilweise direkt ab einem Schweizer Bankkonto (
              <Link allowWordWrap href='789451435515'>
                Lesen Sie hier die ganze Geschichte dazu
              </Link>
              ). In einem Dokument der US-Justiz sind Andrades Besitztümer fein
              säuberlich aufgelistet, die er mit seiner Verurteilung zurückgeben
              musste. Darunter befinden sich acht Immobilien in Florida und
              Venezuela, 17 wertvolle Reitpferde, 10 Autos, 3 Privatjets sowie
              35 Luxusuhren, alle aus Schweizer Produktion.
            </p>
            <p>
              Wie gut kennen Sie sich mit solchen exklusiven Stücken aus? Sehen
              Sie hier fünf von Andrades Uhren und schätzen Sie deren Wert.
            </p>
          </div>
        </div>
      </div>

      <div id='quizSection'>
        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='estimateContainer'>
              <div className='ta text intro'>
                <div className='introText'>{qanda[0].clockType}</div>
                <img
                  src={require(`../../images/jaeger.png`)}
                  className='ui image fluid'
                ></img>
              </div>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID={qanda[0].id}
                question={qanda[0].question}
                correctAnswer={qanda[0].correctAnswer}
                // ignoreLocalStorage
                askTheQuestion={props => (
                  <Slider
                    {...props}
                    range={[0, 300000]}
                    step={5000}
                    unit={qanda[0].unit}
                    start={0}
                  />
                )}
                answerTheQuestion={props => (
                  <>
                    <Summary
                      text={() => {
                        const compare = props.comparison
                        const total = props.answerCount
                        return (
                          <div className='textmessage'>
                            <div
                              className={`message ${
                                !props.answeredCorrectly ? '' : ''
                              }`}
                            >
                              Die richtige Antwort lautet CHF&nbsp;
                              {valueFormat(props.correctAnswer)}.*
                            </div>
                            {props.answeredCorrectly ? (
                              <>
                                <b>{compare.same.share}%</b> der{' '}
                                <b>{total} Teilnehmenden</b>
                                lagen ebenfalls richtig.
                              </>
                            ) : (
                              <>
                                <b>{compare.closer.share}%</b> der{' '}
                                <b>{total} Teilnehmenden</b> waren näher am
                                richtigen Wert.
                              </>
                            )}
                          </div>
                        )
                      }}
                    />
                    <ColumnChart
                      {...props}
                      range={[0, 300000]}
                      step={5000}
                      unit='CHF'
                    />
                    {questionAnswered.includes(props.questionID) && (
                      <div className='description'>{qanda[0].erklaerung}</div>
                    )}
                  </>
                )}
                reportsuccess={handleAnswer}
              />
            </div>
          </div>
        </div>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='estimateContainer'>
              <div className='ta text intro'>
                <div className='introText'>{qanda[1].clockType}</div>
                <img
                  src={require(`../../images/rolex.png`)}
                  className='ui image fluid'
                ></img>
              </div>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID={qanda[1].id}
                question={qanda[1].question}
                correctAnswer={qanda[1].correctAnswer}
                // ignoreLocalStorage
                askTheQuestion={props => (
                  <Slider
                    {...props}
                    range={[0, 300000]}
                    step={5000}
                    unit={qanda[1].unit}
                    start={0}
                  />
                )}
                answerTheQuestion={props => (
                  <>
                    <Summary
                      text={() => {
                        const compare = props.comparison
                        const total = props.answerCount
                        return (
                          <div className='textmessage'>
                            <div
                              className={`message ${
                                !props.answeredCorrectly ? '' : ''
                              }`}
                            >
                              Die richtige Antwort lautet CHF&nbsp;
                              {valueFormat(props.correctAnswer)}.*
                            </div>
                            {props.answeredCorrectly ? (
                              <>
                                <b>{compare.same.share}%</b> der{' '}
                                <b>{total} Teilnehmenden</b>
                                lagen ebenfalls richtig.
                              </>
                            ) : (
                              <>
                                <b>{compare.closer.share}%</b> der{' '}
                                <b>{total} Teilnehmenden</b> waren näher am
                                richtigen Wert.
                              </>
                            )}
                          </div>
                        )
                      }}
                    />
                    <ColumnChart
                      {...props}
                      range={[0, 300000]}
                      step={5000}
                      unit='CHF'
                    />
                    {questionAnswered.includes(props.questionID) && (
                      <div className='description'>{qanda[1].erklaerung}</div>
                    )}
                  </>
                )}
                reportsuccess={handleAnswer}
              />
            </div>
          </div>
        </div>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='estimateContainer'>
              <div className='ta text intro'>
                <div className='introText'>{qanda[2].clockType}</div>
                <img
                  src={require(`../../images/greubel-forsey.png`)}
                  className='ui image fluid'
                ></img>
              </div>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID={qanda[2].id}
                question={qanda[2].question}
                correctAnswer={qanda[2].correctAnswer}
                // ignoreLocalStorage
                askTheQuestion={props => (
                  <Slider
                    {...props}
                    range={[0, 300000]}
                    step={5000}
                    unit={qanda[2].unit}
                    start={0}
                  />
                )}
                answerTheQuestion={props => (
                  <>
                    <Summary
                      text={() => {
                        const compare = props.comparison
                        const total = props.answerCount
                        return (
                          <div className='textmessage'>
                            <div
                              className={`message ${
                                !props.answeredCorrectly ? '' : ''
                              }`}
                            >
                              Die richtige Antwort lautet CHF&nbsp;
                              {valueFormat(props.correctAnswer)}.*
                            </div>
                            {props.answeredCorrectly ? (
                              <>
                                <b>{compare.same.share}%</b> der{' '}
                                <b>{total} Teilnehmenden</b>
                                lagen ebenfalls richtig.
                              </>
                            ) : (
                              <>
                                <b>{compare.closer.share}%</b> der{' '}
                                <b>{total} Teilnehmenden</b> waren näher am
                                richtigen Wert.
                              </>
                            )}
                          </div>
                        )
                      }}
                    />
                    <ColumnChart
                      {...props}
                      range={[0, 300000]}
                      step={5000}
                      unit='CHF'
                    />
                    {questionAnswered.includes(props.questionID) && (
                      <div className='description'>{qanda[2].erklaerung}</div>
                    )}
                  </>
                )}
                reportsuccess={handleAnswer}
              />
            </div>
          </div>
        </div>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='estimateContainer'>
              <div className='ta text intro'>
                <div className='introText'>{qanda[3].clockType}</div>
                <img
                  src={require(`../../images/hublot.png`)}
                  className='ui image fluid'
                ></img>
              </div>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID={qanda[3].id}
                question={qanda[3].question}
                correctAnswer={qanda[3].correctAnswer}
                // ignoreLocalStorage
                askTheQuestion={props => (
                  <Slider
                    {...props}
                    range={[0, 300000]}
                    step={5000}
                    unit={qanda[3].unit}
                    start={0}
                  />
                )}
                answerTheQuestion={props => (
                  <>
                    <Summary
                      text={() => {
                        const compare = props.comparison
                        const total = props.answerCount
                        return (
                          <div className='textmessage'>
                            <div
                              className={`message ${
                                !props.answeredCorrectly ? '' : ''
                              }`}
                            >
                              Die richtige Antwort lautet CHF&nbsp;
                              {valueFormat(props.correctAnswer)}.*
                            </div>
                            {props.answeredCorrectly ? (
                              <>
                                <b>{compare.same.share}%</b> der{' '}
                                <b>{total} Teilnehmenden</b>
                                lagen ebenfalls richtig.
                              </>
                            ) : (
                              <>
                                <b>{compare.closer.share}%</b> der{' '}
                                <b>{total} Teilnehmenden</b> waren näher am
                                richtigen Wert.
                              </>
                            )}
                          </div>
                        )
                      }}
                    />
                    <ColumnChart
                      {...props}
                      range={[0, 300000]}
                      step={5000}
                      unit='CHF'
                    />
                    {questionAnswered.includes(props.questionID) && (
                      <div className='description'>{qanda[3].erklaerung}</div>
                    )}
                  </>
                )}
                reportsuccess={handleAnswer}
              />
            </div>
          </div>
        </div>

        <div className='ui vertical segment'>
          <div className='ui text container'>
            <div className='estimateContainer'>
              <div className='ta text intro'>
                <div className='introText'>{qanda[4].clockType}</div>
                <img
                  src={require(`../../images/patek.png`)}
                  className='ui image fluid'
                ></img>
              </div>
              <EstimateQuestion
                articleID={ARTICLE_ID}
                questionID={qanda[4].id}
                question={qanda[4].question}
                correctAnswer={qanda[4].correctAnswer}
                // ignoreLocalStorage
                askTheQuestion={props => (
                  <Slider
                    {...props}
                    range={[0, 300000]}
                    step={5000}
                    unit={qanda[4].unit}
                    start={0}
                  />
                )}
                answerTheQuestion={props => (
                  <>
                    <Summary
                      text={() => {
                        const compare = props.comparison
                        const total = props.answerCount
                        return (
                          <div className='textmessage'>
                            <div
                              className={`message ${
                                !props.answeredCorrectly ? '' : ''
                              }`}
                            >
                              Die richtige Antwort lautet CHF&nbsp;
                              {valueFormat(props.correctAnswer)}.*
                            </div>
                            {props.answeredCorrectly ? (
                              <>
                                <b>{compare.same.share}%</b> der{' '}
                                <b>{total} Teilnehmenden</b>
                                lagen ebenfalls richtig.
                              </>
                            ) : (
                              <>
                                <b>{compare.closer.share}%</b> der{' '}
                                <b>{total} Teilnehmenden</b> waren näher am
                                richtigen Wert.
                              </>
                            )}
                          </div>
                        )
                      }}
                    />
                    <ColumnChart
                      {...props}
                      range={[0, 300000]}
                      step={5000}
                      unit='CHF'
                    />
                    {questionAnswered.includes(props.questionID) && (
                      <div className='description'>{qanda[4].erklaerung}</div>
                    )}
                  </>
                )}
                reportsuccess={handleAnswer}
              />
            </div>
          </div>
        </div>
      </div>
      <div className='ui vertical segment'>
        <div className='ui text container'>
          <div className='ta text intro'>
            <p>
              * Der Wert der Uhren wurde geschätzt von Marco Gabella, Gründer
              der Website{' '}
              <Link href='https://www.watchonista.com/'>
                Watchonista.com
              </Link>
              . Es handelt sich um den ungefähren Marktwert, der je nach
              Entwicklung variieren kann.
            </p>
          </div>
        </div>
      </div>
      <Footer
        articleId={ARTICLE_ID}
        questions={[
          'Wie unterhaltsam war das Quiz?',
          'Funktionierte die technische Umsetzung?'
        ]}
        credits={[
          ['Konzept und Text', 'Christian Brönnimann, Sylvain Besson'],
          ['Umsetzung', 'Patrick Vögeli']
        ]}
      />
    </div>
  )
}
